import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { X, Move, Music4, Layers, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import useEscapeClose from "@/lib/useEscapeClose";

/* ─────────────────────────────────────────────────────────────────────────
 *  Arrangement View — chapter (c) multi-track timeline.
 *
 *  Channels render as horizontal rows; clips render as draggable blocks
 *  positioned along the bar grid. Drag horizontally to reposition; click
 *  the X to delete; live transport plays everything through the engine.
 *
 *  Data flows:
 *    engine.getAllClips()  → [{channelIdx, id, startBeat, durationSec, name}]
 *    engine.moveClipOnChannel(channelIdx, clipId, newStartBeat)
 *    engine.removeClipFromChannel(channelIdx, clipId)
 *
 *  Pure pointer-event drag; no library needed. Snap-to-beat (1/4 default).
 *  ───────────────────────────────────────────────────────────────────────── */

const BEAT_W = 36;     // px per beat — wider than MIDI editor's 56 since we show many bars
const ROW_H = 38;      // px per channel row
const KEY_COL_W = 92;  // left column reserved for channel name + idx
const SNAP_BEAT = 0.25;

function snap(beat) { return Math.round(beat / SNAP_BEAT) * SNAP_BEAT; }

/** Hash a channel index to a deterministic vivid color so each row's clips
 *  read as belonging together. The 8 hues span the wheel so adjacent indices
 *  contrast cleanly. */
function clipColor(channelIdx) {
  const hues = [340, 18, 50, 110, 165, 200, 250, 290];
  const h = hues[channelIdx % hues.length];
  return `hsl(${h} 75% 55%)`;
}

function ClipBlock({ clip, secPerBeat, channelIdx, isSelected, onSelect, onMove, onDelete }) {
  const widthBeats = clip.durationSec / secPerBeat;
  const left = clip.startBeat * BEAT_W;
  const width = Math.max(28, widthBeats * BEAT_W);
  const color = clipColor(channelIdx);

  // Drag state — pointer events. We compute deltaBeats from clientX deltas.
  const dragRef = useRef({ dragging: false, startX: 0, startBeat: 0 });

  const onPointerDown = (e) => {
    // Allow native delete-button click without starting a drag.
    if (e.target?.closest?.("[data-no-drag]")) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragRef.current = { dragging: true, startX: e.clientX, startBeat: clip.startBeat };
    onSelect?.(clip);
  };
  const onPointerMove = (e) => {
    const st = dragRef.current;
    if (!st.dragging) return;
    const deltaBeats = (e.clientX - st.startX) / BEAT_W;
    const next = Math.max(0, snap(st.startBeat + deltaBeats));
    if (next !== clip.startBeat) onMove?.(clip, next);
  };
  const onPointerUp = (e) => {
    dragRef.current.dragging = false;
    try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch (_) { void _; }
  };

  return (
    <div
      data-testid={`arr-clip-${clip.id}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      style={{
        position: "absolute",
        left, top: 4, width,
        height: ROW_H - 8,
        background: `linear-gradient(135deg, ${color}, ${color}aa)`,
        boxShadow: isSelected
          ? `0 0 0 2px white, 0 0 18px ${color}66`
          : `0 2px 6px rgba(0,0,0,0.3)`,
      }}
      className="cursor-grab select-none rounded-md ring-1 ring-white/15 transition active:cursor-grabbing"
    >
      <div className="flex h-full items-center gap-1 px-2 text-white">
        <Move className="h-3 w-3 flex-shrink-0 opacity-70" />
        <span className="flex-1 truncate font-mono text-[10.5px] uppercase tracking-wider">
          {clip.name || "Clip"}
        </span>
        <button
          type="button"
          data-no-drag
          data-testid={`arr-clip-delete-${clip.id}`}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onDelete?.(clip); }}
          title="Delete clip"
          className="grid h-5 w-5 flex-shrink-0 place-items-center rounded text-white/80 transition hover:bg-white/25"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

export default function ArrangementView({ open, onClose, engine, tempo = 120 }) {
  useEscapeClose(open, onClose);
  const [, forceRender] = useState(0);
  const [selectedClipId, setSelectedClipId] = useState(null);
  const [bars, setBars] = useState(16);
  const beatsPerBar = 4;
  const totalBeats = bars * beatsPerBar;
  const gridWidth = totalBeats * BEAT_W;
  const secPerBeat = 60 / (tempo || 120);

  // Refresh clip list every 400ms while open — cheap, captures live changes
  // from MIDI editor render / uploads without needing to wire effect callbacks.
  useEffect(() => {
    if (!open) return undefined;
    const id = window.setInterval(() => forceRender((n) => n + 1), 400);
    return () => window.clearInterval(id);
  }, [open]);

  const clipsByChannel = useMemo(() => {
    if (!engine) return new Map();
    const map = new Map();
    const all = engine.getAllClips?.() || [];
    all.forEach((c) => {
      if (!map.has(c.channelIdx)) map.set(c.channelIdx, []);
      map.get(c.channelIdx).push(c);
    });
    return map;
    // forceRender pseudo-dep through the parent state — we re-derive whenever the parent re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine, open]);

  // Decide which rows to show: every channel that has clips OR has a primary buffer.
  const rowChannels = useMemo(() => {
    if (!engine?.channels) return [];
    const out = [];
    for (let i = 0; i < engine.channels.length; i++) {
      const ch = engine.channels[i];
      if (ch?.clips?.length || ch?.buffer) {
        out.push({ idx: i, name: ch.name || `CH ${String(i + 1).padStart(2, "0")}`, hasPrimary: !!ch.buffer });
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine, open]);

  const handleMove = useCallback((clip, newStartBeat) => {
    engine?.moveClipOnChannel?.(clip.channelIdx, clip.id, newStartBeat);
    forceRender((n) => n + 1);
  }, [engine]);

  const handleDelete = useCallback((clip) => {
    if (engine?.removeClipFromChannel?.(clip.channelIdx, clip.id)) {
      toast.message(`Removed "${clip.name || "clip"}"`);
      if (selectedClipId === clip.id) setSelectedClipId(null);
      forceRender((n) => n + 1);
    }
  }, [engine, selectedClipId]);

  if (!open) return null;

  const empty = rowChannels.length === 0;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-md"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        data-testid="arrangement-backdrop"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.96 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-[min(96vw,1200px)] max-h-[92vh] overflow-hidden rounded-3xl border border-white/[0.1] bg-gradient-to-br from-zinc-900/95 to-zinc-950/95 shadow-[0_24px_64px_-16px_rgba(0,0,0,0.65)]"
          data-testid="arrangement-modal"
        >
          {/* Header */}
          <header className="flex items-center justify-between gap-3 border-b border-white/[0.07] px-6 py-4">
            <div className="flex items-center gap-3">
              <Layers className="h-5 w-5 text-sky-300" />
              <div>
                <h2 className="font-display text-[18px] font-semibold tracking-tight text-white">Arrangement</h2>
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/45">
                  Drag clips horizontally · click X to delete · transport plays everything in place
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-white/55">
                Bars
                <select
                  data-testid="arr-bars-select"
                  value={bars}
                  onChange={(e) => setBars(Number(e.target.value))}
                  className="rounded-md border border-white/10 bg-black/40 px-2 py-1 font-mono text-[11px] text-white"
                >
                  {[8, 16, 32, 64].map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </label>
              <button
                type="button"
                data-testid="arrangement-close-btn"
                onClick={onClose}
                className="grid h-8 w-8 place-items-center rounded-full border border-white/10 bg-white/[0.04] text-white/65 transition hover:bg-white/[0.1]"
                aria-label="Close arrangement view"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </header>

          {/* Body */}
          {empty ? (
            <div data-testid="arrangement-empty" className="flex flex-col items-center gap-3 px-6 py-16 text-center">
              <Music4 className="h-10 w-10 text-white/30" />
              <h3 className="font-display text-[16px] font-semibold text-white">No clips yet</h3>
              <p className="max-w-md font-body text-[13px] leading-6 text-white/55">
                Upload audio to a channel, render a MIDI clip, or run any AI generation — anything that
                lands on a mixer channel shows up here as a draggable clip on the timeline.
              </p>
            </div>
          ) : (
            <div className="overflow-auto" style={{ maxHeight: "70vh" }}>
              {/* Bar ruler */}
              <div className="sticky top-0 z-20 flex border-b border-white/[0.1] bg-zinc-950/95">
                <div className="flex-shrink-0" style={{ width: KEY_COL_W }} />
                <div className="relative" style={{ width: gridWidth, height: 22 }}>
                  {Array.from({ length: bars + 1 }).map((_, b) => (
                    <div
                      key={`bar-${b}`}
                      className="absolute top-0 flex h-full items-center pl-1 font-mono text-[9.5px] uppercase tracking-wider text-white/45"
                      style={{ left: b * beatsPerBar * BEAT_W }}
                    >
                      {b + 1}
                    </div>
                  ))}
                </div>
              </div>

              {/* Channel rows */}
              {rowChannels.map((row) => {
                const clips = clipsByChannel.get(row.idx) || [];
                return (
                  <div
                    key={`row-${row.idx}`}
                    data-testid={`arr-row-${row.idx}`}
                    className="flex border-b border-white/[0.04] hover:bg-white/[0.015]"
                    style={{ height: ROW_H }}
                  >
                    <div
                      className="flex flex-shrink-0 items-center gap-2 border-r border-white/[0.07] bg-black/40 px-3 font-mono text-[11px] text-white/75"
                      style={{ width: KEY_COL_W }}
                    >
                      <span
                        className="grid h-5 w-5 place-items-center rounded text-[9.5px] font-bold text-white"
                        style={{ background: clipColor(row.idx) }}
                      >
                        {row.idx + 1}
                      </span>
                      <span className="truncate text-[10.5px] uppercase tracking-wider">{row.name}</span>
                    </div>
                    <div
                      className="relative"
                      style={{ width: gridWidth, height: ROW_H }}
                    >
                      {/* Bar grid lines */}
                      {Array.from({ length: bars + 1 }).map((_, b) => (
                        <div
                          key={`grid-${b}`}
                          className="absolute top-0 bg-white/[0.08]"
                          style={{ left: b * beatsPerBar * BEAT_W, width: 1, height: ROW_H }}
                        />
                      ))}
                      {/* Primary buffer rendered as a ghost clip at beat 0
                          (visual-only — playback for the primary buffer goes
                          through the existing channel.start path). */}
                      {row.hasPrimary ? (
                        <div
                          data-testid={`arr-primary-${row.idx}`}
                          className="absolute rounded-md border border-dashed border-white/15 bg-white/[0.05]"
                          style={{
                            left: 0, top: 4,
                            width: Math.max(28, (engine.channels[row.idx].duration / secPerBeat) * BEAT_W),
                            height: ROW_H - 8,
                          }}
                          title="Primary channel buffer — drag in the mixer to manage"
                        >
                          <div className="flex h-full items-center px-2 font-mono text-[9.5px] uppercase tracking-wider text-white/55">
                            {(engine.channels[row.idx].name || "primary").slice(0, 16)}
                          </div>
                        </div>
                      ) : null}
                      {/* Clips */}
                      {clips.map((clip) => (
                        <ClipBlock
                          key={clip.id}
                          clip={clip}
                          secPerBeat={secPerBeat}
                          channelIdx={row.idx}
                          isSelected={selectedClipId === clip.id}
                          onSelect={(c) => setSelectedClipId(c.id)}
                          onMove={handleMove}
                          onDelete={handleDelete}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Footer */}
          <div className="border-t border-white/[0.07] bg-black/30 px-6 py-2 font-mono text-[10px] uppercase tracking-wider text-white/40">
            Tip · drag a clip to move it · 1/4-beat snap · selected clip shows a white outline
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
